import React, { useEffect, useState } from 'react';
import { Form, Button, InputNumber, Switch, Card, Tag, Row, Col, Modal, Select, Checkbox, Divider } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { ArrowForward, CheckCircleOutline, RadioButtonUnchecked } from '@material-ui/icons';
import { ClearOutlined } from '@ant-design/icons';
import toast from 'components/Basic/Toast';
import Loading from 'components/Basic/Loading';
import { SensitiveInput, SecretRefInput } from 'components/Basic/SensitiveInput';
import {
  useLazyGetIntegrationQuery,
  useUpdateIntegrationMutation,
} from 'core/integrations/IntegrationsService';
import { useLazyGetRestaurantListQuery } from 'core/restaurant/RestaurantService';
import { useAppDispatch, useAppSelector } from 'utilities/redux';
import { setRestaurantList } from 'core/restaurant/RestaurantSlice';

const { Option } = Select;

const SLUG = 'sdp';

interface SdpSettings {
  host: string;
  username: string;
  cacheTtl: number;
  timeout: number;
  organizationCode: string;
  restaurantEtaCacheTtlMs: number;
  webhookSecret: string;
  trackingEnabled: boolean;
  sendDriverName: boolean;
  etaBufferEnabled: boolean;
  etaBufferMinutes: number;
  arrivingThresholdMinutes: number;
  minorDelayThresholdMinutes: number;
  highDemandModeEnabled: boolean;
  highDemandMaxMinutes: number;
  customerConfirmationEnabled: boolean;
  customerConfirmTimeoutMinutes: number;
  autoCompleteTimeoutMinutes: number;
  confirmationFallbackEnabled: boolean;
  pilotRestaurantIds: number[];
}

const SdpConfig: React.FC = () => {
  const intl = useIntl();

  const dispatch = useAppDispatch();
  const { list: restaurantList } = useAppSelector((state: any) => state.restaurantReducer);
  const [getRestaurantList] = useLazyGetRestaurantListQuery();

  const [settings, setSettings] = useState<SdpSettings>({
    host: '',
    username: '',
    cacheTtl: 300,
    timeout: 30000,
    organizationCode: '',
    restaurantEtaCacheTtlMs: 300000,
    webhookSecret: '',
    trackingEnabled: true,
    sendDriverName: true,
    etaBufferEnabled: false,
    etaBufferMinutes: 0,
    arrivingThresholdMinutes: 5,
    minorDelayThresholdMinutes: 15,
    highDemandModeEnabled: false,
    highDemandMaxMinutes: 90,
    customerConfirmationEnabled: true,
    customerConfirmTimeoutMinutes: 5,
    autoCompleteTimeoutMinutes: 10,
    confirmationFallbackEnabled: false,
    pilotRestaurantIds: [],
  });
  const [passwordRef, setPasswordRef] = useState('SDP_PASSWORD');
  const [resetKey, setResetKey] = useState(0);
  const [enabled, setEnabled] = useState(false);
  const [initialEnabled, setInitialEnabled] = useState(false);
  const [initialSettings, setInitialSettings] = useState<SdpSettings | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [getIntegration] = useLazyGetIntegrationQuery();
  const [updateIntegration] = useUpdateIntegrationMutation();

  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      getIntegration({ slug: SLUG }).unwrap(),
      getRestaurantList({}).unwrap().then(res => dispatch(setRestaurantList({ list: res }))).catch(() => {}),
    ])
      .then(([res]) => {
        setEnabled(res.enabled);
        setInitialEnabled(res.enabled);
        const s = res.settings ?? {};
        const loaded: SdpSettings = {
          host:                          s.host ?? '',
          username:                      s.username ?? '',
          cacheTtl:                      s.cacheTtl ?? 300,
          timeout:                       s.timeout ?? 30000,
          organizationCode:              s.organizationCode ?? '',
          restaurantEtaCacheTtlMs:       s.restaurantEtaCacheTtlMs ?? 300000,
          webhookSecret:                 s.webhookSecret ?? '',
          trackingEnabled:               s.trackingEnabled ?? true,
          sendDriverName:                s.sendDriverName !== false,
          etaBufferEnabled:              s.etaBufferEnabled ?? false,
          etaBufferMinutes:              s.etaBufferMinutes ?? 0,
          arrivingThresholdMinutes:      s.arrivingThresholdMinutes ?? 5,
          minorDelayThresholdMinutes:    s.minorDelayThresholdMinutes ?? 15,
          highDemandModeEnabled:         s.highDemandModeEnabled ?? false,
          highDemandMaxMinutes:          s.highDemandMaxMinutes ?? 90,
          customerConfirmationEnabled:   s.customerConfirmationEnabled ?? true,
          customerConfirmTimeoutMinutes: s.customerConfirmTimeoutMinutes ?? 5,
          autoCompleteTimeoutMinutes:    s.autoCompleteTimeoutMinutes ?? 10,
          confirmationFallbackEnabled:   s.confirmationFallbackEnabled ?? false,
          pilotRestaurantIds:            Array.isArray(s.pilotRestaurantIds) ? s.pilotRestaurantIds : [],
        };
        setSettings(loaded);
        setInitialSettings(loaded);
        if (s.password?.$secretRef) setPasswordRef(s.password.$secretRef);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [getIntegration, getRestaurantList, dispatch]);

  const handleChange = <K extends keyof SdpSettings>(field: K, value: SdpSettings[K]) =>
    setSettings(prev => ({ ...prev, [field]: value }));

  const onCancel = () => {
    setEnabled(initialEnabled);
    if (initialSettings) setSettings(initialSettings);
    setResetKey(k => k + 1);
  };

  const doSave = async () => {
    setIsLoading(true);
    try {
      await updateIntegration({
        slug: SLUG,
        body: {
          enabled,
          settings: {
            host:                          settings.host,
            username:                      settings.username,
            cacheTtl:                      settings.cacheTtl,
            timeout:                       settings.timeout,
            organizationCode:              settings.organizationCode,
            restaurantEtaCacheTtlMs:       settings.restaurantEtaCacheTtlMs,
            ...(settings.webhookSecret ? { webhookSecret: settings.webhookSecret } : {}),
            trackingEnabled:               settings.trackingEnabled,
            sendDriverName:                settings.sendDriverName,
            etaBufferEnabled:              settings.etaBufferEnabled,
            etaBufferMinutes:              settings.etaBufferMinutes,
            arrivingThresholdMinutes:      settings.arrivingThresholdMinutes,
            minorDelayThresholdMinutes:    settings.minorDelayThresholdMinutes,
            highDemandModeEnabled:         settings.highDemandModeEnabled,
            highDemandMaxMinutes:          settings.highDemandMaxMinutes,
            customerConfirmationEnabled:   settings.customerConfirmationEnabled,
            customerConfirmTimeoutMinutes: settings.customerConfirmTimeoutMinutes,
            autoCompleteTimeoutMinutes:    settings.autoCompleteTimeoutMinutes,
            confirmationFallbackEnabled:   settings.confirmationFallbackEnabled,
            pilotRestaurantIds:            settings.pilotRestaurantIds,
          },
        },
      }).unwrap();
      setInitialEnabled(enabled);
      setInitialSettings(settings);
      setResetKey(k => k + 1);
      toast.success({ title: intl.formatMessage({ id: 'SDP configuration updated successfully!' }) });
    } catch {
      toast.error({ title: intl.formatMessage({ id: 'Error updating SDP configuration' }) });
    } finally {
      setIsLoading(false);
    }
  };

  const onSave = () => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'Save changes' }),
      content: intl.formatMessage({ id: 'Save integration warning' }),
      okText: intl.formatMessage({ id: 'Save' }),
      cancelText: intl.formatMessage({ id: 'Cancel' }),
      className: 'integration_confirm_modal',
      onOk: doSave,
    });
  };

  const statusCardStyle = {
    borderColor: enabled ? '#b7eb8f' : '#ffccc7',
    background: enabled ? '#f6ffed' : '#fff2f0',
    borderRadius: 8,
  };

  return (
    <div className="integrations_layout">
      <Loading visible={isLoading} />

      <div className="integrations_status_card">
        <Card style={statusCardStyle}>
          <div className="status_row">
            <div className="status_info">
              <p className="title" style={{ marginBottom: 4 }}>
                <FormattedMessage id="Enable SDP Integration" />
              </p>
              <div className="status_badge">
                {enabled
                  ? <CheckCircleOutline style={{ fontSize: 15, color: '#52c41a' }} />
                  : <RadioButtonUnchecked style={{ fontSize: 15, color: '#ff4d4f' }} />
                }
                <Tag color={enabled ? 'success' : 'error'} style={{ margin: 0 }}>
                  {enabled
                    ? <FormattedMessage id="Active" />
                    : <FormattedMessage id="Inactive" />
                  }
                </Tag>
              </div>
            </div>
            <Switch
              id="integration-sdp-enabled"
              checked={enabled}
              onChange={setEnabled}
            />
          </div>
        </Card>
      </div>

      {enabled && <div className="integrations_grid">

        <div className="integrations_left_col">

          <Card
            title={<FormattedMessage id="Connection" />}
            size="small"
            style={{ borderRadius: 8 }}
          >
            <Form layout="vertical" colon={false}>
              <Form.Item label={<span className="title" style={{ marginBottom: 0 }}><FormattedMessage id="Host URL" /></span>} style={{ marginBottom: 14 }}>
                <SensitiveInput
                  id="integration-sdp-host"
                  key={`host-${resetKey}`}
                  value={settings.host}
                  placeholder="https://iungo.example.com"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('host', e.target.value)}
                />
              </Form.Item>

              <Form.Item label={<span className="title" style={{ marginBottom: 0 }}><FormattedMessage id="Username" /></span>} style={{ marginBottom: 14 }}>
                <SensitiveInput
                  id="integration-sdp-username"
                  key={`username-${resetKey}`}
                  value={settings.username}
                  placeholder="Enter username"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('username', e.target.value)}
                />
              </Form.Item>

              <Form.Item
                label={<span className="title" style={{ marginBottom: 0 }}><FormattedMessage id="Password" /></span>}
                extra={<FormattedMessage id="Value is managed as a server environment variable." />}
                style={{ marginBottom: 14 }}
              >
                <SecretRefInput envVarName={passwordRef} />
              </Form.Item>

              <Form.Item
                label={<span className="title" style={{ marginBottom: 0 }}><FormattedMessage id="Webhook Secret" /></span>}
                extra={<FormattedMessage id="Token SDP must send in the Authorization header when calling the order-status webhook." />}
                style={{ marginBottom: 14 }}
              >
                <SensitiveInput
                  id="integration-sdp-webhook-secret"
                  key={`webhookSecret-${resetKey}`}
                  value={settings.webhookSecret}
                  placeholder="Enter webhook secret"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('webhookSecret', e.target.value)}
                />
              </Form.Item>

              <Form.Item
                label={<span className="title" style={{ marginBottom: 0 }}><FormattedMessage id="Organization Code" /></span>}
                extra={<FormattedMessage id="SDP organization code used to query average service times per restaurant." />}
                style={{ marginBottom: 14 }}
              >
                <SensitiveInput
                  id="integration-sdp-organization-code"
                  key={`organizationCode-${resetKey}`}
                  value={settings.organizationCode}
                  placeholder="Enter organization code"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('organizationCode', e.target.value)}
                />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label={<span className="title" style={{ marginBottom: 0 }}><FormattedMessage id="Cache TTL (seconds)" /></span>} style={{ marginBottom: 0 }}>
                    <InputNumber
                      id="integration-sdp-cache-ttl"
                      min={60} max={3600}
                      value={settings.cacheTtl}
                      onChange={(v: number | null) => handleChange('cacheTtl', v ?? 300)}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label={<span className="title" style={{ marginBottom: 0 }}><FormattedMessage id="Timeout (milliseconds)" /></span>} style={{ marginBottom: 0 }}>
                    <InputNumber
                      id="integration-sdp-timeout"
                      min={5000} max={60000} step={1000}
                      value={settings.timeout}
                      onChange={(v: number | null) => handleChange('timeout', v ?? 30000)}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                colon={false}
                label={<span className="title" style={{ marginBottom: 0 }}><FormattedMessage id="ETA Cache TTL (seconds)" /></span>}
                extra={<FormattedMessage id="How long average service times per restaurant are cached before re-fetching from SDP." />}
                style={{ marginBottom: 0, marginTop: 14 }}
              >
                <InputNumber
                  id="integration-sdp-eta-cache-ttl"
                  min={60} max={3600}
                  value={Math.round(settings.restaurantEtaCacheTtlMs / 1000)}
                  onChange={(v: number | null) => handleChange('restaurantEtaCacheTtlMs', (v ?? 300) * 1000)}
                  style={{ width: '100%' }}
                  addonAfter={<FormattedMessage id="s" />}
                />
              </Form.Item>
            </Form>
          </Card>

          <Card
            title={<FormattedMessage id="Customer Confirmation" />}
            size="small"
            style={{ borderRadius: 8 }}
          >
            <div className="section_toggle_row" style={{ marginBottom: settings.customerConfirmationEnabled ? 16 : 0 }}>
              <div className="section_toggle_label">
                <p className="title"><FormattedMessage id="Customer Order Confirmation" /></p>
                <p className="description">
                  <FormattedMessage id="Customer must confirm receipt before the order is automatically closed." />
                </p>
              </div>
              <Switch
                id="integration-sdp-customer-confirmation"
                checked={settings.customerConfirmationEnabled}
                onChange={(v: boolean) => handleChange('customerConfirmationEnabled', v)}
              />
            </div>

            {settings.customerConfirmationEnabled && (
              <>
                <Row gutter={16} style={{ marginBottom: 16 }}>
                  <Col span={12}>
                    <Form.Item
                      colon={false}
                      label={<span className="title" style={{ marginBottom: 0 }}><FormattedMessage id="Request confirmation after" /></span>}
                      extra={<FormattedMessage id="Minutes from driver marking delivery until the customer is asked to confirm." />}
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber
                        id="integration-sdp-confirm-timeout"
                        min={1} max={60}
                        value={settings.customerConfirmTimeoutMinutes}
                        onChange={(v: number | null) => handleChange('customerConfirmTimeoutMinutes', v ?? 5)}
                        style={{ width: '100%' }}
                        addonAfter={<FormattedMessage id="min" />}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      colon={false}
                      label={<span className="title" style={{ marginBottom: 0 }}><FormattedMessage id="Auto-complete after" /></span>}
                      extra={<FormattedMessage id="Order closes automatically if the customer does not respond within this time." />}
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber
                        id="integration-sdp-auto-complete-timeout"
                        min={1} max={120}
                        value={settings.autoCompleteTimeoutMinutes}
                        onChange={(v: number | null) => handleChange('autoCompleteTimeoutMinutes', v ?? 10)}
                        style={{ width: '100%' }}
                        addonAfter={<FormattedMessage id="min" />}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <div className="section_toggle_row" style={{ marginBottom: 0 }}>
                  <div className="section_toggle_label">
                    <p className="title"><FormattedMessage id="Push fallback if not connected" /></p>
                    <p className="description">
                      <FormattedMessage id="If the customer is not in the app when the timer expires, they receive a push notification to confirm delivery." />
                    </p>
                  </div>
                  <Switch
                    id="integration-sdp-confirmation-fallback"
                    checked={settings.confirmationFallbackEnabled}
                    onChange={(v: boolean) => handleChange('confirmationFallbackEnabled', v)}
                  />
                </div>
              </>
            )}
          </Card>

        </div>

        <div className="integrations_right_col">

          <Card
            title={<FormattedMessage id="Tracking Link" />}
            size="small"
            style={{ borderRadius: 8 }}
          >
            <div className="section_toggle_row" style={{ marginBottom: settings.trackingEnabled ? 16 : 0 }}>
              <div className="section_toggle_label">
                <p className="title"><FormattedMessage id="Enable Tracking Link" /></p>
                <p className="description">
                  <FormattedMessage id="Disable if SDP is unavailable or estimated times are unreliable." />
                </p>
              </div>
              <Switch
                id="integration-sdp-tracking-enabled"
                checked={settings.trackingEnabled}
                onChange={(v: boolean) => handleChange('trackingEnabled', v)}
              />
            </div>

            {settings.trackingEnabled && (
              <>
                <Divider style={{ margin: '8px 0 16px' }} />

                <div className="section_toggle_row" style={{ marginBottom: 0 }}>
                  <div className="section_toggle_label">
                    <p className="title"><FormattedMessage id="Show driver name" /></p>
                    <p className="description">
                      <FormattedMessage id="Shows the driver's name to the customer. Turn it off to always show a generic label instead." />
                    </p>
                  </div>
                  <Switch
                    id="integration-sdp-send-driver-name"
                    checked={settings.sendDriverName}
                    onChange={(v: boolean) => handleChange('sendDriverName', v)}
                  />
                </div>
              </>
            )}
          </Card>

          <Card
            title={<FormattedMessage id="ETA Buffer" />}
            size="small"
            style={{ borderRadius: 8 }}
          >
            <div className="section_toggle_row" style={{ marginBottom: settings.etaBufferEnabled ? 16 : 8 }}>
              <div className="section_toggle_label">
                <p className="title"><FormattedMessage id="Enable ETA Buffer" /></p>
                <p className="description">
                  <FormattedMessage id="Shows a delivery time range instead of an exact time." />
                </p>
              </div>
              <Switch
                id="integration-sdp-eta-buffer-enabled"
                checked={settings.etaBufferEnabled}
                onChange={(v: boolean) => handleChange('etaBufferEnabled', v)}
              />
            </div>

            {settings.etaBufferEnabled && (
              <Form.Item
                colon={false}
                label={<span className="title" style={{ marginBottom: 0 }}><FormattedMessage id="ETA Buffer" /></span>}
                extra={intl.formatMessage({ id: "Buffer active — adds {minutes} min to SDP's estimate." }, { minutes: settings.etaBufferMinutes })}
                style={{ marginBottom: 16 }}
              >
                <InputNumber
                  id="integration-sdp-eta-buffer-minutes"
                  min={0} max={60}
                  value={settings.etaBufferMinutes}
                  onChange={(v: number | null) => handleChange('etaBufferMinutes', v ?? 0)}
                  style={{ width: '100%' }}
                  addonAfter={<FormattedMessage id="min" />}
                />
              </Form.Item>
            )}

            <Divider style={{ margin: '0 0 16px' }} />

            <div className={settings.trackingEnabled ? '' : 'dimmed'}>
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={12}>
                  <Form.Item
                    colon={false}
                    label={<span className="title" style={{ marginBottom: 0 }}><FormattedMessage id="Arriving threshold" /></span>}
                    extra={<FormattedMessage id="Minutes remaining from which the driver is considered to be arriving." />}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber
                      id="integration-sdp-arriving-threshold"
                      min={1} max={30}
                      disabled={!settings.trackingEnabled}
                      value={settings.arrivingThresholdMinutes}
                      onChange={(v: number | null) => handleChange('arrivingThresholdMinutes', v ?? 5)}
                      style={{ width: '100%' }}
                      addonAfter={<FormattedMessage id="min" />}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    colon={false}
                    label={<span className="title" style={{ marginBottom: 0 }}><FormattedMessage id="Delay threshold" /></span>}
                    extra={<FormattedMessage id="Delay over the promised time from which a delay notice is shown to the customer." />}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber
                      id="integration-sdp-delay-threshold"
                      min={1} max={120}
                      disabled={!settings.trackingEnabled}
                      value={settings.minorDelayThresholdMinutes}
                      onChange={(v: number | null) => handleChange('minorDelayThresholdMinutes', v ?? 15)}
                      style={{ width: '100%' }}
                      addonAfter={<FormattedMessage id="min" />}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <div className="section_toggle_row" style={{ marginBottom: settings.highDemandModeEnabled ? 16 : 0 }}>
                <div className="section_toggle_label">
                  <p className="title"><FormattedMessage id="High Demand Mode" /></p>
                  <p className="description">
                    <FormattedMessage id="Activates the high demand notice for all active orders." />
                  </p>
                </div>
                <Switch
                  id="integration-sdp-high-demand-mode"
                  disabled={!settings.trackingEnabled}
                  checked={settings.highDemandModeEnabled}
                  onChange={(v: boolean) => handleChange('highDemandModeEnabled', v)}
                />
              </div>

              {settings.highDemandModeEnabled && (
                <Form.Item
                  colon={false}
                  label={<span className="title" style={{ marginBottom: 0 }}><FormattedMessage id="Max estimated time" /></span>}
                  extra={<FormattedMessage id="Shown in the high demand notice." />}
                  style={{ marginBottom: 0 }}
                >
                  <InputNumber
                    id="integration-sdp-high-demand-max"
                    min={30} max={480}
                    value={settings.highDemandMaxMinutes}
                    onChange={(v: number | null) => handleChange('highDemandMaxMinutes', v ?? 90)}
                    style={{ width: '100%' }}
                    addonAfter={<FormattedMessage id="min" />}
                  />
                </Form.Item>
              )}
            </div>
          </Card>

        </div>
      </div>}

      {enabled && (
        <div className="integrations_pilot_section">
          <Card
            title={<FormattedMessage id="Pilot Restaurants" />}
            size="small"
            style={{ borderRadius: 8, marginTop: 16 }}
          >
            <p className="description" style={{ marginBottom: 12 }}>
              <FormattedMessage id="Customers from selected restaurants see the tracking link. Leave empty to enable for all." />
            </p>
            <div className="selection-header" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <Checkbox
                checked={
                  restaurantList?.rows?.length > 0 &&
                  settings.pilotRestaurantIds.length === restaurantList.rows.length
                }
                onChange={e => {
                  const ids = e.target.checked
                    ? (restaurantList?.rows ?? []).map((r: any) => r.id)
                    : [];
                  handleChange('pilotRestaurantIds', ids);
                }}
              >
                <FormattedMessage id="Select all" />
              </Checkbox>
              <Button
                id="integration-sdp-pilot-clear"
                className="btn_clear"
                onClick={() => handleChange('pilotRestaurantIds', [])}
              >
                <FormattedMessage id="Clear selection" />
                <ClearOutlined onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
              </Button>
            </div>
            <Select
              id="integration-sdp-pilot-restaurants"
              showSearch
              mode="multiple"
              style={{ width: '100%' }}
              placeholder={intl.formatMessage({ id: 'Select restaurant' })}
              value={settings.pilotRestaurantIds}
              onChange={(values: number[]) => handleChange('pilotRestaurantIds', values)}
              filterOption={(input, option) =>
                String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
              }
              getPopupContainer={trigger => trigger.parentNode}
            >
              {(restaurantList?.rows ?? []).map((item: any) => (
                <Option value={item.id} key={item.id}>
                  {item.description}
                </Option>
              ))}
            </Select>
            {settings.pilotRestaurantIds.length === 0 && (
              <p className="description" style={{ marginTop: 8, color: '#faad14' }}>
                <FormattedMessage id="No restaurants selected — tracking link is enabled for all." />
              </p>
            )}
          </Card>
        </div>
      )}

      <div className="integrations_actions">
        <Button id="integration-sdp-save" onClick={onSave}>
          <FormattedMessage id="Save" />
          <ArrowForward />
        </Button>
        <p id="integration-sdp-cancel" className="integrations_cancel_btn" onClick={onCancel}>
          <FormattedMessage id="Cancel" />
        </p>
      </div>

    </div>
  );
};

export default SdpConfig;
